import { useState, useEffect, useCallback } from 'react'
import { authClient } from '../../lib/auth-client'
import { getOffers, openOfferPdf } from '../../services/offerService'
import type { Offer, OfferType } from '../../types/offer'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Alert, AlertDescription } from '../ui/alert'
import { Badge } from '../ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Link } from 'react-router-dom'
import { FileText, Loader2, Plus, Pencil, Printer } from 'lucide-react'
import { CreateOfferModal } from './CreateOfferModal'

const OPEN_STATUS = 'active'

export function OffersTab() {
  const { data: session, isPending: isSessionPending } = authClient.useSession()
  const [offers, setOffers] = useState<Offer[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<OfferType | 'all'>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [printingOfferId, setPrintingOfferId] = useState<string | null>(null)
  const [printError, setPrintError] = useState<string | null>(null)

  const loadOffers = useCallback(async () => {
    if (!session?.user) {
      setOffers([])
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const data = await getOffers(typeFilter === 'all' ? undefined : typeFilter)
      setOffers(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load offers')
      setOffers([])
    } finally {
      setIsLoading(false)
    }
  }, [session?.user, typeFilter])

  useEffect(() => {
    loadOffers()
  }, [loadOffers])

  const openOffers = offers.filter((o) => o.status === OPEN_STATUS)
  const formatCurrency = (value: number | null | undefined) => {
    if (value == null) return '—'
    return `$${value.toFixed(2)}`
  }

  const getPositionCount = (offer: Offer) => {
    if (offer.type === 'Sell') return offer.sellPositions?.length ?? 0
    return offer.buyPositions?.length ?? 0
  }

  const getTotalValue = (offer: Offer) => {
    if (offer.type === 'Sell') {
      return offer.sellPositions?.reduce((sum, p) => sum + (p.price ?? 0), 0) ?? 0
    }
    return offer.buyPositions?.reduce((sum, p) => sum + (p.price ?? 0), 0) ?? 0
  }

  const handlePrintOffer = async (offerId: string) => {
    setPrintError(null)
    setPrintingOfferId(offerId)
    try {
      await openOfferPdf(offerId)
    } catch (err) {
      setPrintError(err instanceof Error ? err.message : 'Failed to open print preview')
    } finally {
      setPrintingOfferId(null)
    }
  }

  if (isSessionPending || !session?.user) {
    return (
      <Card className="min-h-[400px]">
        <CardHeader>
          <CardTitle className="text-3xl text-blue-800">Offers</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground">Please sign in to view your offers.</p>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card className="min-h-[400px]">
        <CardHeader>
          <CardTitle className="text-3xl text-blue-800">Offers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading offers...</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="min-h-[400px]">
        <CardHeader>
          <CardTitle className="text-3xl text-blue-800">Offers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-4 py-12">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button onClick={loadOffers} className="bg-blue-800 text-white hover:bg-blue-900">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  const isEmpty = openOffers.length === 0

  return (
    <Card className="min-h-[400px]">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="text-3xl text-blue-800">Open Offers</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as OfferType | 'all')}>
              <SelectTrigger className="w-[140px] bg-white border-gray-300">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Buy">Buy</SelectItem>
                <SelectItem value="Sell">Sell</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-800 text-white hover:bg-blue-900"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Offer
            </Button>
          </div>
        </div>
      </CardHeader>
      <CreateOfferModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={loadOffers}
      />
      <CardContent>
        {printError && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{printError}</AlertDescription>
          </Alert>
        )}
        {isEmpty && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-4">
              <FileText className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-gray-900">No open offers</h3>
            <p className="text-muted-foreground">Create offers from your inventory or collection to buy or sell cards.</p>
          </div>
        )}

        {!isEmpty && (
          <div className="overflow-x-auto -mx-4 md:mx-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Positions</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[88px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {openOffers.map((offer) => (
                  <TableRow key={offer.id}>
                    <TableCell>
                      <span className="font-medium text-gray-900">{offer.title || 'Untitled offer'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={offer.type === 'Sell' ? 'default' : 'secondary'}
                        className={offer.type === 'Sell' ? 'bg-emerald-600 text-white' : 'bg-blue-100 text-blue-800 border-blue-200'}
                      >
                        {offer.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                        {offer.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{getPositionCount(offer)}</TableCell>
                    <TableCell className="font-medium">{formatCurrency(getTotalValue(offer))}</TableCell>
                    <TableCell className="text-muted-foreground">{new Date(offer.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-0.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-gray-600 hover:text-gray-900"
                          aria-label="Print offer"
                          disabled={printingOfferId === offer.id}
                          onClick={() => void handlePrintOffer(offer.id)}
                        >
                          {printingOfferId === offer.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Printer className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                          className="text-gray-600 hover:text-gray-900"
                          aria-label="Edit offer"
                        >
                          <Link to={`/offers/${offer.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
